import { Anchor } from '@vtex/admin-ui'
import { Radio, RadioGroup } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ListPriceKeys } from 'typings'
import { ListPriceConfig } from '.'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../..'
import { GroupLabel } from '../../../GroupLabel'
import { RadioDescription } from '../../../Radio/RadioDescription'
import { messages } from './messages'

type ListPriceFormObject = {
  listPrice: ListPriceKeys
}

const EMPTY_CONFIG = Object.freeze({ listPrice: ListPriceKeys.NOT_DEFINED })

const LIST_PRICE = 'listPrice'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: ListPriceConfig
  onSave: (config: ListPriceConfig) => void
}

export function ListPriceEditor(props: Props) {
  const { state, onSave, initialConfig } = props
  const { hide } = state

  const { $t } = useIntl()

  const mountedInitialValue = useMemo(
    () =>
      initialConfig ? { [LIST_PRICE]: initialConfig } : { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<ListPriceFormObject>({
    config: mountedInitialValue,
    isOpen: state.open,
  })

  const handleSave = async () => {
    const values = drawerForm.getValues()
    if (values && !!values.listPrice) {
      onSave(values.listPrice)
    }
    hide()
  }

  const isCreating = initialConfig === undefined

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>
          {$t(messages.subtitle, {
            a: (content) => (
              <Anchor target="_blank" href={$t(messages.moreInformation)}>
                {content}
              </Anchor>
            ),
          })}
        </DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <RadioGroup
          name={LIST_PRICE}
          label={<GroupLabel>{$t(messages.editor.radioGroup)}</GroupLabel>}
          state={drawerForm}
        >
          {Object.values(ListPriceKeys).map((value) => (
            <Radio
              key={value}
              label={
                <RadioDescription
                  title={$t(messages.editor[value].title)}
                  description={$t(messages.editor[value].description)}
                />
              }
              value={value}
            />
          ))}
        </RadioGroup>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
